'use client';

import { Chip } from '@sprd/sprd-component-kit/v2';
import { AnimationEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import type { Locale } from 'use-intl/core';
import DynamicIcon, { isDynamicIcon } from '@/components/Common/DynamicIcon';
import { NonIndexableLink } from '@/components/NonIndexableLink/NonIndexableLink';
import Slider from '@/components/Slider/Slider';
import { getPathname, useRouter } from '@/i18n/navigation';
import { mpProductGridLoadingAtom } from '@/store/atoms';
import { getMPListPageHrefArgs } from '@/utility/urls';
import type { DesignThemes, DesignTopic } from '../utility/designThemes';
import { MarketplaceSearchParams } from '../utility/validationSchema';

type MPDesignThemesTransitionProps = {
  designThemes: DesignThemes;
  departmentSlug?: string | undefined;
  categorySlugs?: string[] | undefined;
  filterParams?: MarketplaceSearchParams | undefined;
};

const themesKey = (themes: DesignTopic[]) => themes.map((theme) => theme.name).join('|');

type DesignThemeRowProps = DesignThemes & {
  departmentSlug?: string | undefined;
  categorySlugs?: string[] | undefined;
  filterParams?: MarketplaceSearchParams | undefined;
  locale: Locale;
  onThemeClick: (theme: DesignTopic) => (event: MouseEvent<HTMLAnchorElement>) => void;
};

const DesignThemeRow = ({
  themes,
  level,
  selectedTheme,
  departmentSlug,
  categorySlugs,
  filterParams,
  locale,
  onThemeClick,
}: DesignThemeRowProps) => {
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const handleProgressChange = (progress: number) => {
    setCanScrollPrev(progress > 0);
    setCanScrollNext(progress < 1);
  };

  return (
    // The slider owns the list: it renders the `ul` and wraps every child in its own `li`. Item
    // widths stay at the chips' own width — `--slides-to-show` is for equal-width cards.
    <div className="group relative">
      <Slider
        showNavigation
        noPadding
        snap={false}
        onProgressChange={handleProgressChange}
        slidesToScroll="auto"
        className={{
          item: 'basis-auto!',
          navPrev:
            'left-0! hidden! text-neutral-900 shadow-none! hover:bg-neutral-200 hover:shadow-md md:flex! md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:focus-visible:opacity-100',
          navNext:
            'right-0! hidden! text-neutral-900 shadow-none! hover:bg-neutral-200 hover:shadow-md md:flex! md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:focus-visible:opacity-100',
        }}
      >
        {themes.map((theme) => {
          const selected = theme === selectedTheme;
          const icon = isDynamicIcon(theme.icon) ? (
            <DynamicIcon icon={theme.icon} aria-hidden="true" className="size-5" />
          ) : null;
          const label = (
            <div className="flex items-center gap-1">
              {icon}
              {theme.name}
            </div>
          );

          // The theme the search term matched: the PLP already shows it, so there is nothing to
          // link to. It uses the primary variant rather than the topic color — the kit switches a
          // chip to its custom palette as soon as `color` is set, which would ignore the variant.
          if (selected) {
            return (
              <Chip
                key={theme.name}
                asChild
                selected
                border="none"
                variant="primary"
                className="block px-3! whitespace-nowrap"
              >
                <span aria-current="page">{label}</span>
              </Chip>
            );
          }

          // A topic's fixed URL for the current locale, if any, takes priority over the search-term
          const themeUrl = theme.url?.[locale];

          return (
            <Chip
              key={theme.name}
              asChild
              selected={false}
              color={theme.color}
              border="none"
              className="block px-3! whitespace-nowrap"
            >
              <NonIndexableLink
                href={
                  themeUrl ??
                  getPathname({
                    href: getMPListPageHrefArgs({
                      departmentSlug,
                      categorySlugs,
                      searchTerm: theme.name,
                      queryParams: filterParams,
                    }),
                    locale,
                  })
                }
                prefetch={false}
                onClick={themeUrl ? undefined : onThemeClick(theme)}
                data-tracking={`MP Design Theme - c${level} - ${theme.name}`}
              >
                {label}
              </NonIndexableLink>
            </Chip>
          );
        })}
      </Slider>
      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute inset-y-0 left-0 hidden w-20 bg-linear-to-r from-white via-white/90 to-transparent opacity-0 transition-opacity md:block',
          canScrollPrev && 'md:group-hover:opacity-100'
        )}
      />
      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute inset-y-0 right-0 hidden w-20 bg-linear-to-l from-white via-white/90 to-transparent opacity-0 transition-opacity md:block',
          canScrollNext && 'md:group-hover:opacity-100'
        )}
      />
    </div>
  );
};

export const MPDesignThemesTransition = ({
  designThemes,
  departmentSlug,
  categorySlugs,
  filterParams,
}: MPDesignThemesTransitionProps) => {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('mpProductListPage');

  const setProductGridLoading = useSetAtom(mpProductGridLoadingAtom);

  // The row on screen, and the row it's
  // replacing, kept mounted just long enough to play its own exit fade. Both are set directly by the
  // click that starts a navigation
  const [current, setCurrent] = useState(designThemes);
  const [exiting, setExiting] = useState<DesignThemes | null>(null);
  const confirmedDesignThemesRef = useRef(designThemes);
  // Starts false on every mount, flips true only inside a click.
  const hasAnimatedRef = useRef(false);

  // Reconciles with the real navigation once it lands. Silent, not animated: `current` already shows
  // what the click predicted, so this only swaps in the confirmed object reference, except for a
  // navigation that changes the strip without a chip click at all (a filter, the back button, ...),
  // where it's the only thing updating `current`.
  useEffect(() => {
    if (designThemes === confirmedDesignThemesRef.current) {
      return;
    }
    confirmedDesignThemesRef.current = designThemes;
    setCurrent(designThemes);
  }, [designThemes]);

  const handleThemeClick = (theme: DesignTopic) => (event: MouseEvent<HTMLAnchorElement>) => {
    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

    // A modified click opens the real href in a new tab — nothing to intercept.
    if (modified) {
      return;
    }

    event.preventDefault();

    const next: DesignThemes = theme.children.length
      ? { themes: theme.children, level: current.level + 1 }
      : { themes: current.themes, level: current.level, selectedTheme: theme };

    // Only a genuinely different row (drilling into children) fades the outgoing row out and animates
    // the incoming one in. Selecting a sibling leaf keeps the same row — just a different chip marked
    // selected — with no animation at all: `hasAnimatedRef` must stay untouched here, or flipping it
    // false→true on a leaf click would newly apply the entry animation class to this unchanged row,
    // which starts it playing even though nothing about the row itself changed.
    if (themesKey(next.themes) !== themesKey(current.themes)) {
      setExiting(current);
      hasAnimatedRef.current = true;
    }
    setCurrent(next);
    setProductGridLoading(true);

    router.push(
      getMPListPageHrefArgs({
        departmentSlug,
        categorySlugs,
        searchTerm: theme.name,
        queryParams: filterParams,
      })
    );
  };

  // Clears the exiting row once its own fade-out has actually finished
  const handleExitAnimationEnd = (event: AnimationEvent<HTMLDivElement>) => {
    if (event.animationName === 'design-themes-out') {
      setExiting(null);
    }
  };

  return (
    <>
      <h2 className="pb-2 font-inter text-base font-normal text-neutral-700">
        {current.level === 1 ? t('popularTopics') : t('popularRelatedTopics')}
      </h2>
      {/* One grid cell holds both rows, so the entering row takes the exiting one's place without the
          strip changing height while they crossfade. */}
      <div className="grid">
        {exiting ? (
          <div
            key={themesKey(exiting.themes)}
            aria-hidden="true"
            inert
            onAnimationEnd={handleExitAnimationEnd}
            className="col-start-1 row-start-1 min-w-0 motion-safe:animate-design-themes-out motion-reduce:hidden"
          >
            <DesignThemeRow
              {...exiting}
              departmentSlug={departmentSlug}
              categorySlugs={categorySlugs}
              filterParams={filterParams}
              locale={locale}
              onThemeClick={handleThemeClick}
            />
          </div>
        ) : null}
        <div
          key={themesKey(current.themes)}
          className={clsx(
            'col-start-1 row-start-1 min-w-0',
            hasAnimatedRef.current && 'motion-safe:animate-design-themes-in'
          )}
        >
          <DesignThemeRow
            {...current}
            departmentSlug={departmentSlug}
            categorySlugs={categorySlugs}
            filterParams={filterParams}
            locale={locale}
            onThemeClick={handleThemeClick}
          />
        </div>
      </div>
    </>
  );
};
